/* Run orchestration hook.
 *
 * Owns the per-version `PhpRuntime` instances (lazily created, reused across
 * runs) and the results/running state the panel renders. Selected versions run
 * through a bounded worker pool (3–4 concurrent) so that selecting many versions
 * never spins up one heavy wasm instance per version all at once. */

import { useCallback, useRef, useState } from 'preact/hooks';
import { PlaygroundRuntime } from '../../runtime/playground-runtime';
import type { PhpRuntime } from '../../runtime/php-runtime';
import { RunAbortError } from '../../runtime/run-result';
import { runPool } from '../../runtime/worker-pool';
import { toRunDisplay, errorDisplay } from './run-result-display';
import type { ResultMap, RunningMap } from '../results/result-types';

export interface RunInput {
  stdin?: string;
  args?: string;
}

export interface RunController {
  results: ResultMap;
  running: RunningMap;
  isRunning: boolean;
  run: (code: string, versions: string[], input?: RunInput) => Promise<void>;
  /** Idle-warm a version's worker so its first run skips the wasm compile. */
  warm: (version: string) => void;
  cancel: () => void;
}

export function useRunController(): RunController {
  const [results, setResults] = useState<ResultMap>({});
  const [running, setRunning] = useState<RunningMap>({});
  const [isRunning, setIsRunning] = useState(false);

  const runtimes = useRef(new Map<string, PhpRuntime>());
  const abortRef = useRef<AbortController | null>(null);

  const getRuntime = (v: string): PhpRuntime => {
    let rt = runtimes.current.get(v);
    if (!rt) {
      rt = new PlaygroundRuntime(v);
      runtimes.current.set(v, rt);
    }
    return rt;
  };

  const run = useCallback(async (code: string, versions: string[], input: RunInput = {}) => {
    if (!versions.length) return;

    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setIsRunning(true);
    setRunning(Object.fromEntries(versions.map((v) => [v, true])));
    // Prior output stays visible during the run (cleared output collapses the pane
    // and causes a jump). Each version is overwritten in place when its result lands.
    // Pool caps concurrency; per-version error handling so one failure doesn't stall others.
    await runPool(versions, async (v) => {
      if (ac.signal.aborted) {
        setRunning((prev) => ({ ...prev, [v]: false }));
        return;
      }
      try {
        const result = await getRuntime(v).run(code, {
          stdin: input.stdin,
          args: input.args,
          signal: ac.signal,
        });
        setResults((prev) => ({ ...prev, [v]: toRunDisplay(result) }));
      } catch (err) {
        // Drop the runtime so the next run re-inits a fresh worker.
        runtimes.current.delete(v);
        if (!(err instanceof RunAbortError && err.kind === 'cancel')) {
          const msg =
            err instanceof RunAbortError
              ? err.message
              : `PHP ${v} run error: ${err instanceof Error ? err.message : String(err)}`;
          setResults((prev) => ({ ...prev, [v]: errorDisplay(msg) }));
        }
      } finally {
        setRunning((prev) => ({ ...prev, [v]: false }));
      }
    });

    // Only the run that still owns abortRef clears shared flags — a superseded run
    // must not mark the app idle while the newer run is still in flight.
    if (abortRef.current === ac) {
      setRunning({});
      setIsRunning(false);
      abortRef.current = null;
    }
  }, []);

  const warm = useCallback((version: string) => {
    void getRuntime(version).warm();
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    setRunning({});
    setIsRunning(false);
  }, []);

  return { results, running, isRunning, run, warm, cancel };
}
